import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  ListItemText,
  OutlinedInput,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import ClearIcon from "@mui/icons-material/Clear";
import SearchWithDebounce from "./SearchWithDebounce";
import { FixedSizeList } from "react-window";
import { TooltipProps } from "../../../helpers/materialUITheme";
import { Arrow } from "../icons/Arrow";

const ITEM_HEIGHT = 50;
const ITEM_PADDING_TOP = 13;
const SEARCH_HEIGHT = 72;
const VISIBLE_ITEMS = 4;
const SelectSimpleWithClientSearch = ({
  options,
  label,
  onChange,
  onCustomChange,
  error,
  value,
  isCustomNull,
  labelKey,
  placeholder,
  itemValue,
  displayEmpty = false,
  showSearch = true,
  setValue,
  isSpecialValueReturn = false,
  disabled,
  withClosedIcon,
  // searchValue,
  // setSearchValue,
  ...rest
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options || []);
  const availableWidth = 600 - 40 - 16 - 24;
  useEffect(() => {
    setFilteredOptions(options || []);
  }, [options]);

  useEffect(() => {
    if (searchValue) {
      const filtered = (options || []).filter((option) => {
        const optionText = (
          option.name?.toString() ||
          option?.[labelKey] ||
          option ||
          ""
        ).toLowerCase();
        return optionText.includes(searchValue.toLowerCase());
      });
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options || []);
    }
  }, [searchValue, options, labelKey]);
  const measureTextWidth = (text, font = "16px Inter") => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    context.font = font;
    return context.measureText(text).width;
  };
  const MenuProps = {
    autoFocus: false,
    PaperProps: {
      style: {
        minWidth: "600px",
        maxHeight:
          filteredOptions?.length >= 4
            ? (ITEM_HEIGHT + ITEM_PADDING_TOP) * 4 + SEARCH_HEIGHT
            : (ITEM_HEIGHT + ITEM_PADDING_TOP) * filteredOptions?.length +
              SEARCH_HEIGHT,
      },
    },
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchValue(""); // Очищаем поиск при закрытии
  };

  const handleChange = (v) => {
    onChange(v);
    onCustomChange && onCustomChange(v);
    setIsOpen(false);
    setSearchValue("");
  };

  const handleClearValue = () => {};

  const renderMenuItems = useCallback(
    ({ index, style }) => {
      const option = filteredOptions[index];
      const text = option?.name;
      const textWidth = measureTextWidth(text);
      const isOverflowed = textWidth > availableWidth;
      return (
        <MenuItem
          key={option.id || index}
          value={
            !isSpecialValueReturn
              ? option?.value ||
                option?.[itemValue] ||
                option?.id ||
                option?.name ||
                option ||
                ""
              : JSON.stringify({
                  id: option.value,
                  name: option.name,
                  ...option,
                })
          }
          style={{
            ...style,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "560px",
          }}
          onClick={() => {
            handleChange(
              !isSpecialValueReturn
                ? option?.value ||
                    option?.[itemValue] ||
                    option?.id ||
                    option?.name ||
                    option ||
                    ""
                : JSON.stringify({
                    id: option.value,
                    name: option.name,
                    ...option,
                  })
            );
          }}
        >
          {isOverflowed ? (
            <Tooltip
              title={text}
              componentsProps={TooltipProps}
              followCursor
              placement="bottom"
              arrow
            >
              <ListItemText
                primary={
                  option.name?.toString() || option?.[labelKey] || option || ""
                }
              />
            </Tooltip>
          ) : (
            <ListItemText
              primary={
                option.name?.toString() || option?.[labelKey] || option || ""
              }
            />
          )}
        </MenuItem>
      );
    },
    [filteredOptions, labelKey]
  );

  const renderValue = (selected) => {
    if ((!selected && selected !== 0) || selected === t(placeholder)) {
      return <span style={{ color: "#a1a8b0" }}>{t(placeholder)}</span>;
    }
    const selectedValue = isSpecialValueReturn
      ? JSON.parse(selected)?.id
      : selected;
    const selectedOption = options?.find(
      (opt) =>
        String(opt?.value) === String(selectedValue) ||
        String(opt?.id) === String(selectedValue) ||
        opt?.name === selectedValue
    );

    return (
      <span style={{ color: "#030c23" }}>
        {selectedOption?.name || selectedOption?.regulationName || selected}
      </span>
    );
  };

  return (
    <Box sx={{ position: "relative" }} className="input_text">
      <InputLabel sx={{ mb: "8px" }}>{t(label)}</InputLabel>
      <FormControl fullWidth error={!!error} {...rest} disabled={disabled}>
        <Select
          value={value || t(placeholder)}
          input={<OutlinedInput />}
          MenuProps={MenuProps}
          displayEmpty={displayEmpty}
          sx={{
            ".MuiSelect-select": { height: 0, color: "#a1a8b0" },
            ".MuiListItemText-root": {
              margin: "0",
            },
            maxHeight: "48px",
          }}
          open={isOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          renderValue={renderValue}
          IconComponent={() =>
            !disabled &&
            (withClosedIcon ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => {
                  value !== t(label)
                    ? handleClearValue()
                    : setIsOpen((prev) => !prev);
                }}
              >
                {value !== t(label) ? (
                  <ClearIcon sx={{ fontSize: 22 }} />
                ) : (
                  <Arrow />
                )}
              </Box>
            ) : (
              <Box
                sx={{ cursor: !disabled ? "pointer" : "default" }}
                onClick={() =>
                  !disabled ? setIsOpen((prev) => !prev) : () => {}
                }
              >
                {isOpen ? <Arrow /> : <Arrow />}
              </Box>
            ))
          }
        >
          {showSearch && (
            <SearchWithDebounce
              searchValue={searchValue}
              setSearchValue={setSearchValue}
            />
          )}
          {displayEmpty && !searchValue && (
            <MenuItem
              sx={{ height: ITEM_HEIGHT + ITEM_PADDING_TOP }}
              key={"none"}
              value=""
            >
              {t(placeholder) || "Нет"}
            </MenuItem>
          )}
          {filteredOptions?.length > 0 ? (
            <FixedSizeList
              height={
                Math.min(filteredOptions.length, VISIBLE_ITEMS) * ITEM_HEIGHT
              }
              itemCount={filteredOptions.length}
              itemSize={ITEM_HEIGHT}
              width="600px"
              style={{ overflowX: "hidden" }}
            >
              {renderMenuItems}
            </FixedSizeList>
          ) : (
            <MenuItem disabled sx={{ height: ITEM_HEIGHT + ITEM_PADDING_TOP }}>
              <ListItemText
                primary={<Typography>{t("no_results")}</Typography>}
              />
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </Box>
  );
};

export default SelectSimpleWithClientSearch;
